// Pure scoring of a qualification test correction (phonemes, phrases,
// translations), extracted from useGetPaid without changing the rules.

export interface SubmissionDetails {
  userId: string;
  userName: string;
  testId: string;
  language: string;
  phonemes: { id: string; phoneme: string; audioUrl: string; score: number | null }[];
  phrases: { id: string; local: string; french?: string; audioUrl: string; score: number | null }[];
  translations: { id: string; local: string; userTranslation: string; score: number | null }[];
}

export type ItemScores = Record<string, number | undefined>;

/** The correction screen's items, read from the submission's answers. */
export function buildSubmissionDetails(test: any): SubmissionDetails {
  const answers = test?.answers || {};
  const phonemeList: any[] = Array.isArray(answers.phonemes) ? answers.phonemes : [];
  const phraseList: any[] = Array.isArray(answers.phrases) ? answers.phrases : [];

  return {
    userId: test.userId || test.id,
    userName: test.userName || test.title || test.userId,
    testId: test.id,
    language: test.language,
    phonemes: phonemeList.map((p: any, i: number) => ({
      id: `ph-${i}`,
      phoneme: typeof p === "string" ? p : (p.symbol || p.phoneme || ""),
      audioUrl: (p && (p.sasUrl || p.audioUrl)) || "",
      score: null,
    })),
    phrases: phraseList.map((pp: any, i: number) => ({
      id: `pp-${i}`,
      local: pp.local,
      french: pp.french,
      audioUrl: (pp && (pp.sasUrl || pp.audioUrl)) || "",
      score: null,
    })),
    translations: phraseList
      .filter((pp: any) => typeof pp.userAnswer === "string")
      .map((pp: any, i: number) => ({
        id: `tp-${i}`,
        local: pp.local,
        userTranslation: pp.userAnswer || "",
        score: null,
      })),
  };
}

/** 0 to 10 by half points; null when the input is not a number. */
export function clampScore(value: string): number | null {
  let v = parseFloat(value);
  if (Number.isNaN(v)) return null;
  if (v < 0) v = 0;
  if (v > 10) v = 10;
  return Math.round(v * 2) / 2;
}

/** Average per section; an unscored section counts 0. */
export function scoreAverages(details: SubmissionDetails | null, scores: ItemScores) {
  const avg = (ids: string[]) => {
    const vals = ids.map((id) => scores[id]).filter((n): n is number => typeof n === "number");
    if (vals.length === 0) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };
  if (!details) return { ph: 0, pp: 0, tp: 0 };
  return {
    ph: avg(details.phonemes.map((p) => p.id)),
    pp: avg(details.phrases.map((p) => p.id)),
    tp: avg(details.translations.map((t) => t.id)),
  };
}

/** Phonemes 30 %, phrases 30 %, translations 40 %. */
export const weightedTotal = (a: { ph: number; pp: number; tp: number }) => a.ph * 0.3 + a.pp * 0.3 + a.tp * 0.4;

export function allItemsScored(details: SubmissionDetails | null, scores: ItemScores) {
  if (!details) return false;
  return [
    ...details.phonemes.map((p) => p.id),
    ...details.phrases.map((p) => p.id),
    ...details.translations.map((t) => t.id),
  ].every((id) => typeof scores[id] === "number");
}

/** Below 5 out of 10 the test is rejected. */
export const CORRECTION_PASS_MARK = 5;
